import React, { useEffect, useState } from 'react';
import { FaGift, FaCopy, FaCheck } from 'react-icons/fa';
import { getReferralLink, trackEvent } from '../api/client';

/**
 * The referral ask, made at the moment the first itinerary finishes.
 *
 * The rewards and referral machinery has existed for a while and has been
 * effectively invisible: it lives on a page nobody visits, and nothing ever
 * points at it. This puts it in front of the traveller at the one moment they
 * are demonstrably pleased — the plan they asked for has just appeared.
 *
 * It renders nothing when the account has no active referral link, so a user
 * whose plan doesn't carry referral credit is never shown an offer that would
 * not pay out.
 */
const ReferralPrompt = ({ destination, className = '' }) => {
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getReferralLink()
      .then((data) => {
        if (!cancelled && data?.active && data?.referral_url) setReferral(data);
      })
      .catch(() => {
        // A missing referral link is not an error worth showing anyone.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!referral) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referral.referral_url);
      setCopied(true);
      trackEvent('referral_link_copied', { surface: 'itinerary_complete', destination });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard blocked — the link is on screen and selectable anyway.
    }
  };

  return (
    <div
      className={`mt-6 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 px-6 py-5 ${className}`}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
          <FaGift className="text-amber-600" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-neutral-900">
            Know someone else planning a trip?
          </h3>
          <p className="mt-1 text-sm leading-relaxed text-neutral-600">
            Share your link and you both earn travel points when they plan their first
            trip.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs text-neutral-700">
              {referral.referral_url}
            </code>
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-600"
            >
              {copied ? <FaCheck size={12} /> : <FaCopy size={12} />}
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralPrompt;
